import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

/**
 * Frontend-side mirror of the backend ``paid_access_guard`` middleware.
 *
 * Returns everything the caller needs to gray out write actions BEFORE the
 * server has a chance to return 402, so the practitioner gets an immediate,
 * unmissable feedback that they need to subscribe.
 *
 * Only enforced for admins right now — assistants/research users inherit the
 * cabinet owner's paid state on the backend, but the frontend only knows about
 * the currently signed-in user's flags. The axios 402 interceptor covers those
 * edge cases with a toast when they attempt a write.
 */
export function useAccessGate() {
  const { user } = useAuth();
  const nav = useNavigate();

  const isTrialExpired =
    user?.role === "admin"
    && user?.subscription_status === "trialing"
    && !!user?.trial_ends_at
    && new Date(user.trial_ends_at).getTime() <= Date.now();

  /**
   * Spread on a Button/Link to disable it when the trial is expired and route
   * the click to the billing page instead. Preserves any caller-provided
   * disabled/onClick/title.
   *
   *   <Button {...gateProps({ onClick: submit, title: "Envoyer" })}>Envoyer</Button>
   */
  const gateProps = ({ onClick, disabled, title } = {}) => {
    if (!isTrialExpired) return { onClick, disabled, title };
    return {
      disabled: true,
      title: title
        ? `${title} — Souscrivez à un abonnement pour continuer`
        : "Souscrivez à un abonnement pour continuer",
      "aria-disabled": true,
      "data-gate": "trial-expired",
      onClick: (e) => {
        e?.preventDefault?.();
        nav("/settings?tab=billing");
      },
    };
  };

  return { isTrialExpired, gateProps };
}
